// ADR 30.9.26d §2 — the remembered app-language choice, and what is shown.
//
// A BOOT MODULE: `index.ts` and `api/http.ts` import it, so it must never
// import `../wasm` (25.9.26f; `wasm/__tests__/bootGraph.test.ts` fails it) and
// it imports nothing but `resolve.ts` — no i18next either, so `http.ts` reading
// the header costs the request path nothing.
//
// Two snapshots: *shown* (the locale the app displays, which the
// `Accept-Language` header follows) and *chosen* (the resolved stored choice,
// or null while following the device). `initI18n` seeds both with
// `recordLaunch`; `setAppLanguage` moves both with `recordSwitch`. Every storage
// access is in try/catch: Safari's private mode and a disabled-storage profile
// throw, and a throw is "no choice" (the `ui/theme.ts` rule). The key survives
// sign-out — `auth/signOutWipe.ts` keeps UI preferences.

import {
  browserLocales,
  pickLocale,
  resolveLocale,
  type LocaleSource,
  type SupportedLocale,
} from './resolve'

export type { LocaleSource }

export const LOCALE_KEY = 'lingara.locale'

type Launch = { locale: SupportedLocale; source: LocaleSource }

let launch: Launch | null = null
let shown: SupportedLocale | null = null
let chosen: SupportedLocale | null = null
const listeners = new Set<() => void>()

function defaultStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** The raw stored value, or null when absent or unreadable. Not resolved: chooseLocale does that. */
export function readStoredLocale(storage?: Pick<Storage, 'getItem'>): string | null {
  try {
    return (storage ?? defaultStorage())?.getItem(LOCALE_KEY) ?? null
  } catch {
    return null
  }
}

/** Store or remove the choice (try/catch); returns false when storage refused. */
export function writeStoredLocale(code: SupportedLocale | null, storage?: Storage): boolean {
  try {
    const target = storage ?? defaultStorage()
    if (target === null) return false
    if (code === null) target.removeItem(LOCALE_KEY)
    else target.setItem(LOCALE_KEY, code)
    return true
  } catch {
    return false
  }
}

/** The locale the device asks for, ignoring ?lng= and any stored choice. */
export function deviceLocale(preferred: readonly string[] = browserLocales()): SupportedLocale {
  return pickLocale(preferred)
}

/** The browser's default `Intl` locale — what the formatting sites used before 30.9.26d. */
export function defaultIntlLocale(): string {
  return Intl.DateTimeFormat().resolvedOptions().locale
}

/** What initI18n resolved, and why. Null before it ran. Read by §7. */
export function launchLocale(): Launch | null {
  return launch
}

/** The locale now shown, or null before initI18n. Read by §4's Accept-Language. */
export function appLanguageHeader(): SupportedLocale | null {
  return shown
}

/** The resolved stored choice, or null while following the device. */
export function chosenLocale(): SupportedLocale | null {
  return chosen
}

/** `useSyncExternalStore`'s subscribe over the *chosen* snapshot. */
export function subscribeChosen(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** `initI18n`'s seed: the launch result, and the stored choice as resolved. */
export function recordLaunch(result: Launch, stored: string | null): void {
  launch = result
  shown = result.locale
  chosen = stored ? resolveLocale(stored) : null
  listeners.forEach((listener) => listener())
}

/** `setAppLanguage`'s update, after it persisted (or failed to). */
export function recordSwitch(nowShown: SupportedLocale, nowChosen: SupportedLocale | null): void {
  shown = nowShown
  chosen = nowChosen
  listeners.forEach((listener) => listener())
}

/** For suites that render without `initI18n`: back to "never launched". */
export function resetLocaleStoreForTests(): void {
  launch = null
  shown = null
  chosen = null
  listeners.forEach((listener) => listener())
}
